// Stopka – CMS (FOOTER: CONTACT, HELP, ABOUT) + certyfikaty + Znajdziesz nas (CMS) na dole + powered by
// @ts-nocheck

import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { Container } from 'components/controls';
import { useGetHomeHtmlBlock } from 'api/endpoints';
import { Plus, Dash } from 'react-bootstrap-icons';

import isoCertImage from 'assets/iso-cert.png';
import haccpCertImage from 'assets/haccp-cert.png';
import senteLogo from 'assets/logo-sente.svg';

import styles from 'theme/components/layouts/MainLayout/components/Footer/Footer.module.scss';

const FOOTER_ARTICLES = [
  { id: 'FOOTER_CONTACT', titleKey: 'Skontaktuj się z nami', sectionKey: 'contactUs' },
  { id: 'FOOTER_HELP', titleKey: 'Pomoc', sectionKey: 'support' },
  { id: 'FOOTER_ABOUT', titleKey: 'O firmie', sectionKey: 'info' },
] as const;

const Footer = () => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const toggleSection = (section: string) =>
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );

  const { data: contactData } = useGetHomeHtmlBlock('FOOTER', 'FOOTER_CONTACT');
  const { data: helpData } = useGetHomeHtmlBlock('FOOTER', 'FOOTER_HELP');
  const { data: aboutData } = useGetHomeHtmlBlock('FOOTER', 'FOOTER_ABOUT');
  const { data: socialData } = useGetHomeHtmlBlock('FOOTER', 'FOOTER_SOCIAL');

  const columns = useMemo(() => {
    const toHtml = (items: { section_field_symbol: string; html_code: string }[] | undefined) => {
      if (!items?.length) return '';
      const x = items.find((i) => i.section_field_symbol === 'content');
      return (x?.html_code ?? items[0]?.html_code) || '';
    };
    return [
      { html: toHtml(contactData?.items), ...FOOTER_ARTICLES[0] },
      { html: toHtml(helpData?.items), ...FOOTER_ARTICLES[1] },
      { html: toHtml(aboutData?.items), ...FOOTER_ARTICLES[2] },
    ];
  }, [contactData?.items, helpData?.items, aboutData?.items]);

  const socialHtml = useMemo(() => {
    const items = socialData?.items;
    if (!items?.length) return '';
    const x = items.find((i) => i.section_field_symbol === 'content');
    return (x?.html_code ?? items[0]?.html_code) || '';
  }, [socialData?.items]);

  return (
    <div
      className={classnames(styles.componentWrapper, 'StylePath-Components-Layouts-MainLayout-Components-Footer')}
      itemScope
      itemType="http://schema.org/Organization">
      <meta itemProp="url" content={window.location.href} />
      <Container>
        <div className={styles.contentWrapper}>
          <div>
            {columns.map(({ id, titleKey, sectionKey, html }) => (
              <section key={id}>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleSection(sectionKey)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleSection(sectionKey)}
                  className={classnames(styles.title, { [styles.open]: expandedSections.includes(sectionKey) })}>
                  {t(titleKey)}
                  {expandedSections.includes(sectionKey) ? <Dash /> : <Plus />}
                </span>
                <div
                  className={classnames(styles.cmsColumn, { [styles.hidden]: !expandedSections.includes(sectionKey) })}>
                  {html ? <div dangerouslySetInnerHTML={{ __html: html }} /> : null}
                </div>
              </section>
            ))}
          </div>

          <div>
            <section>
              <span
                role="button"
                tabIndex={0}
                onClick={() => toggleSection('certificates')}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleSection('certificates')}
                className={classnames(styles.title, styles.certificatesTitle, {
                  [styles.open]: expandedSections.includes('certificates'),
                })}>
                <Trans>Jakość potwierdzona certyfikatami</Trans>
              </span>
              <div
                className={classnames(styles.certificatesWrapper, {
                  [styles.hidden]: !expandedSections.includes('certificates'),
                })}>
                <img src={isoCertImage} alt="ISO Certified Company" className={styles.certificate} />
                <img src={haccpCertImage} alt="HACCP Food Safety Certified" className={styles.certificate} />
              </div>
            </section>
          </div>
        </div>
      </Container>

      <div className={styles.bottomBar}>
        <Container>
          <div className={styles.contentWrapper}>
            {socialHtml ? (
              <div className={styles.lists}>
                <section className={styles.socialCms} dangerouslySetInnerHTML={{ __html: socialHtml }} />
              </div>
            ) : null}
            <section className={styles.powered}>
              powered by
              <a href="https://sente.pl" target="_blank" rel="noreferrer">
                <img src={senteLogo} alt="logo Sente" />
                <meta itemProp="logo" content={senteLogo} />
              </a>
            </section>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default Footer;
